import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { addOnAPI, trekAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './ModuleAdmin.css';
import { formatAdminInr } from '../utils/formatAdminPrice.js';

const AddOns = () => {
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', trekId: '', search: '' });
  const trekFilter = query.trekId || '';
  const search = query.search || '';
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const { permissions } = useAuth();
  const canManageAddOns = permissions.includes('*') || permissions.includes('content_manage');

  const { data: treksData } = useQuery(['treks'], () => trekAPI.list({ limit: 200 }));
  const treks = treksData?.data?.data || [];

  const { data, isLoading } = useQuery(
    ['add-ons', trekFilter, search, page, limit],
    () => addOnAPI.list({ trekId: trekFilter || undefined, search: search || undefined, page, limit })
  );
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: items.length };

  const deleteMutation = useMutation((id) => addOnAPI.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(['add-ons'])
  });

  const handleDelete = (item) => {
    if (!window.confirm('Delete add-on "' + item.name + '"?')) return;
    deleteMutation.mutate(item.id);
  };


  return (
    <div className="module-page">
      <PageHeader
        title="Add-ons"
        subtitle="Manage optional add-ons for bookings. Attach to a trek or leave blank for all treks."
        actions={canManageAddOns ? <Link to="/add-ons/new" className="module-btn">Add</Link> : null}
      />
      <FilterBar>
        <input
          type="text"
          className="module-filter-input"
          placeholder="Search add-ons..."
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
        />
        <select className="module-filter-select" value={trekFilter} onChange={(e) => setQuery({ trekId: e.target.value })}>
          <option value="">All add-ons</option>
          {treks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading add-ons...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No add-ons yet"
            description="Optional extras for bookings — attach to a trek or leave blank for all treks."
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Trek</th>
                <th>Type</th>
                <th>Price</th>
                {canManageAddOns && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name || '-'}</td>
                  <td>{item.trek ? item.trek.name : 'All treks'}</td>
                  <td>{item.type || 'gear'}</td>
                  <td>{formatAdminInr(item.price)}</td>
                  {canManageAddOns && (
                    <td className="module-actions">
                      <Link to={'/add-ons/' + item.id} className="module-link-btn">Edit</Link>
                      <button type="button" className="module-link-btn danger" onClick={() => handleDelete(item)} disabled={deleteMutation.isLoading}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="module-list-pagination">
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <div className="module-list-pagination-btns">
                <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                <button type="button" className="module-btn secondary" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddOns;
